import React, { useEffect, useRef } from 'react';
import * as fabric from 'fabric';

// --- FABRIC EXTENSIONS ---
// Override Polyline render to support curved paths if data.isCurved is true
const originalPolylineRender = fabric.Polyline.prototype._render;
fabric.Polyline.prototype._render = function (ctx) {
    const curvedPoints = this.data?.curvedPoints || {};
    const hasAnyCurve = Object.values(curvedPoints).some(v => v);
    const globalCurved = this.data?.isCurved;

    if ((globalCurved || hasAnyCurve) && this.points.length > 2) {
        const p = this.points;
        const offset = this.pathOffset || { x: 0, y: 0 };
        ctx.beginPath();
        ctx.moveTo(p[0].x - (offset.x || 0), p[0].y - (offset.y || 0));

        for (let i = 1; i < p.length - 1; i++) {
            const isCurved = globalCurved || curvedPoints[i];
            const xc = (p[i].x + p[i + 1].x) / 2;
            const yc = (p[i].y + p[i + 1].y) / 2;

            if (isCurved) {
                ctx.quadraticCurveTo(
                    p[i].x - (offset.x || 0),
                    p[i].y - (offset.y || 0),
                    xc - (offset.x || 0),
                    yc - (offset.y || 0)
                );
            } else {
                ctx.lineTo(p[i].x - (offset.x || 0), p[i].y - (offset.y || 0));
                ctx.lineTo(xc - (offset.x || 0), yc - (offset.y || 0));
            }
        }
        ctx.lineTo(
            p[p.length - 1].x - (offset.x || 0),
            p[p.length - 1].y - (offset.y || 0)
        );
        this._renderStroke(ctx);
    } else {
        originalPolylineRender.call(this, ctx);
    }
};

const PlayThumbnail = ({ canvasData, width = 200, height = 300 }) => {
    const canvasRef = useRef(null);
    const fabricCanvas = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !canvasData) return;
        let isDisposed = false;

        // Use standard Canvas but disable all interactions for preview
        const canvas = new fabric.Canvas(canvasRef.current, {
            width: 800,
            height: 800, // Make it square to match the field aspect ratio
            backgroundColor: '#1b4332',
            willReadFrequently: true,
            interactive: false,
            selection: false,
            skipTargetFind: true
        });
        fabricCanvas.current = canvas;

        // Scaling logic: scale the internal canvas coordinates to the display width
        const scale = width / 800;
        canvas.setZoom(scale);
        canvas.setWidth(width);
        canvas.setHeight(width * (height / width));

        // Load Background and Data
        fabric.Image.fromURL('/vertical-field.svg').then((img) => {
            if (isDisposed) return;

            img.set({
                originX: 'left',
                originY: 'top',
                selectable: false,
                evented: false,
                scaleX: 800 / img.width,
                scaleY: 900 / img.height,
            });
            canvas.backgroundImage = img;

            try {
                let data = typeof canvasData === 'string' ? JSON.parse(canvasData) : canvasData;

                // 1. ANALYZE SAVED BACKGROUND METRICS
                const savedObjects = data.objects || [];
                // More robust check: Background image is usually the one with the specific URL or largest
                const savedBg = data.backgroundImage || savedObjects.find(o =>
                    (o.type === 'image' || o.type === 'Image') && (o.width > 500 || (o.src && o.src.includes('field')))
                );

                console.log("[Thumbnail] Saved Background Detected:", savedBg ? "YES" : "NO", savedBg?.type);

                let savedBgLeft = 0, savedBgTop = 0, savedBgScaleX = 1, savedBgScaleY = 1;
                if (savedBg) {
                    savedBgLeft = savedBg.left || 0;
                    savedBgTop = savedBg.top || 0;
                    savedBgScaleX = savedBg.scaleX || 1;
                    savedBgScaleY = savedBg.scaleY || 1;
                    console.log(`[Thumbnail] Saved Metrics: L:${savedBgLeft}, T:${savedBgTop}, S:${savedBgScaleX.toFixed(3)}`);
                }

                // 2. FILTER BACKGROUND FROM OBJECTS
                data.objects = savedObjects.filter(o => {
                    if (savedBg && o === savedBg) return false;
                    if ((o.type === 'image' || o.type === 'Image') && o.width > 500) return false;
                    return true;
                });

                // Clear and Prepare Canvas
                canvas.clear();
                canvas.backgroundColor = '#1b4332';
                canvas.backgroundImage = img;

                // Load Filtered Objects
                canvas.loadFromJSON(data).then(() => {
                    if (isDisposed) return;

                    // Current Background Metrics
                    const currentBgLeft = img.left || 0;
                    const currentBgTop = img.top || 0;
                    const currentBgScaleX = img.scaleX || 1;
                    const currentBgScaleY = img.scaleY || 1;

                    // Calculate Ratios
                    const ratioX = currentBgScaleX / savedBgScaleX;
                    const ratioY = currentBgScaleY / savedBgScaleY;
                    console.log(`[Thumbnail] Ratios: X:${ratioX.toFixed(3)}, Y:${ratioY.toFixed(3)}`);

                    // 6. Transform Objects to Match New Field
                    // We use two passes: 1. Players (Anchors), 2. Everything else
                    const playerCenters = new Map();

                    // --- PASS 1: Players (Anchors) ---
                    canvas.getObjects().forEach(obj => {
                        if (obj === img) return;
                        const type = obj.data?.type;
                        if (type === 'O' || type === 'D') {
                            const relX = obj.left - savedBgLeft;
                            const relY = obj.top - savedBgTop;

                            const sX = (obj.scaleX || 1) * ratioX;
                            const sY = (obj.scaleY || 1) * ratioY;

                            const newLeft = currentBgLeft + (relX * ratioX);
                            const newTop = currentBgTop + (relY * ratioY);

                            obj.set({
                                left: newLeft,
                                top: newTop,
                                scaleX: sX,
                                scaleY: sY
                            });
                            obj.setCoords();

                            // Calculate Absolute Center based on origin
                            let cX = newLeft;
                            let cY = newTop;
                            if (obj.originX === 'left') cX += (obj.width * sX) / 2;
                            else if (obj.originX === 'right') cX -= (obj.width * sX) / 2;

                            if (obj.originY === 'top') cY += (obj.height * sY) / 2;
                            else if (obj.originY === 'bottom') cY -= (obj.height * sY) / 2;

                            if (obj.data?.id) {
                                const id = String(obj.data.id).trim();
                                playerCenters.set(id, { x: cX, y: cY });
                            }
                        }
                    });

                    // --- PASS 2: Routes and Others ---
                    canvas.getObjects().forEach(obj => {
                        if (obj === img) return;
                        if (obj.data?.type === 'O' || obj.data?.type === 'D') return;

                        const sX = (obj.scaleX || 1) * ratioX;
                        const sY = (obj.scaleY || 1) * ratioY;

                        if (obj.data?.type === 'route_polyline' && obj.points && obj.points.length > 0) {
                            // Link via ID or parentId (Designer uses both depending on subType)
                            const id = String(obj.data.id || '').trim();
                            const parentId = String(obj.data.parentId || '').trim();
                            const anchor = playerCenters.get(id) || playerCenters.get(parentId);

                            if (anchor) {
                                // PRECISE ANCHORING: Align points[0] to player center
                                const p0 = obj.points[0];
                                const po = obj.pathOffset || { x: 0, y: 0 };

                                obj.set({
                                    left: anchor.x - (p0.x - (po.x || 0)) * sX,
                                    top: anchor.y - (p0.y - (po.y || 0)) * sY,
                                    scaleX: sX,
                                    scaleY: sY,
                                    originX: 'left',
                                    originY: 'top',
                                    fill: 'transparent',
                                    objectCaching: false
                                });
                                console.log(`[Thumbnail] Anchored route ${id || parentId} to player`);
                            } else {
                                // Fallback relative scaling
                                const relX = obj.left - savedBgLeft;
                                const relY = obj.top - savedBgTop;
                                obj.set({
                                    left: currentBgLeft + (relX * ratioX),
                                    top: currentBgTop + (relY * ratioY),
                                    scaleX: sX,
                                    scaleY: sY
                                });
                            }
                        } else {
                            // General objects (Zones, Arrowheads, LOS etc)
                            const relX = obj.left - savedBgLeft;
                            const relY = obj.top - savedBgTop;
                            obj.set({
                                left: currentBgLeft + (relX * ratioX),
                                top: currentBgTop + (relY * ratioY),
                                scaleX: sX,
                                scaleY: sY
                            });
                        }

                        obj.setCoords();
                    });

                    canvas.backgroundImage = img; // Ensure background is on top of stack post-clear
                    canvas.renderAll();
                });
            } catch (e) {
                console.error("Thumbnail render error:", e);
                if (!isDisposed) canvas.renderAll();
            }
        });

        return () => {
            isDisposed = true;
            canvas.dispose();
            fabricCanvas.current = null;
        };
    }, [canvasData, width, height]);

    return (
        <div
            className="w-full h-full overflow-hidden bg-[#06140d] flex items-center justify-center"
        >
            <canvas ref={canvasRef} />
        </div>
    );
};

export default React.memo(PlayThumbnail);
